import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import renderMarkdown from '../../utils/renderMarkdown';
import { formatTime } from '../../utils/formatTime';
import { Panel, Button, Badge } from '../../components/design';

const CORE_API = '/api';

export default function AiAnalysisTab({ serverId, server, runTroubleshoot }) {
  const [aiMode, setAiMode] = useState('fast');
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [dots, setDots] = useState('.');

  useEffect(() => {
    if (!aiAnalysisLoading) return;
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '.' : prev + '.');
    }, 400);
    return () => clearInterval(interval);
  }, [aiAnalysisLoading]);

  const { data: historyData } = useQuery({
    queryKey: ['servers', serverId, 'ai-analyses'],
    queryFn: () => fetch(`${CORE_API}/servers/${serverId}/ai-analyses`).then(r => r.ok ? r.json() : { analyses: [] }),
    enabled: !!serverId,
    retry: 0,
  });
  const analyses = historyData?.analyses || [];

  const runAiAnalysis = async () => {
    setAiAnalysisLoading(true);
    setAiResult(null);
    try {
      const res = await fetch(`${CORE_API}/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: aiMode.toUpperCase(),
          service: server?.name || '',
          prompt: `Analyze server ${server?.name} (${server?.ip_address}). Status: ${server?.status}. OS: ${server?.os_type}. Check for any issues, performance problems, and security concerns. Provide actionable recommendations.`,
          incident_id: null,
        }),
      });
      const data = await res.json();
      setAiResult(data);
    } catch (e) {
      setAiResult({ error: e.message });
    }
    setAiAnalysisLoading(false);
  };

  const handleTroubleshoot = async () => {
    setAiAnalysisLoading(true);
    const result = await runTroubleshoot();
    setAiResult(result);
    setAiAnalysisLoading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Panel title="AI Engine Analysis">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 16 }}>
          {[
            { id: 'fast', label: 'Fast', desc: 'Quick SRE analysis' },
            { id: 'deep_rca', label: 'Deep RCA', desc: 'Root cause analysis' },
            { id: 'security', label: 'Security', desc: 'Security analysis' },
            { id: 'autonomous_fix', label: 'Autonomous Fix', desc: 'Generate fix commands' },
          ].map(mode => (
            <button key={mode.id} onClick={() => setAiMode(mode.id)}
              style={{
                padding: 12, borderRadius: 12, textAlign: 'left', cursor: 'pointer',
                background: 'var(--glass)', border: aiMode === mode.id ? '1px solid var(--accent-1)' : '1px solid var(--glass-border)',
              }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: aiMode === mode.id ? 'var(--accent-1)' : 'var(--text-1)' }}>{mode.label}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{mode.desc}</div>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="primary" onClick={runAiAnalysis} disabled={aiAnalysisLoading}>
            {aiAnalysisLoading ? `Processing${dots}` : 'Run Analysis'}
          </Button>
          <Button variant="ghost" onClick={handleTroubleshoot} disabled={aiAnalysisLoading}>
            {aiAnalysisLoading ? `Checking${dots}` : 'Troubleshoot'}
          </Button>
        </div>
      </Panel>

      {aiResult && (
        <Panel title={aiResult.mode === 'TROUBLESHOOT' ? 'Troubleshoot Results' : 'Analysis Result'}>
          {aiResult.error ? (
            <div style={{ padding: '10px 14px', borderRadius: 10, fontSize: 12, background: 'rgba(248,113,113,0.10)', color: 'var(--danger)' }}>{aiResult.error}</div>
          ) : (
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>Mode: {aiResult.mode} &bull; {aiResult.timestamp ? formatTime(aiResult.timestamp) : ''}</div>
              <div style={{ fontSize: 14, color: 'var(--text-2)', background: 'var(--glass)', border: '1px solid var(--glass-border)', borderRadius: 12, padding: 16, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(aiResult.result || JSON.stringify(aiResult)) }} />
            </div>
          )}
        </Panel>
      )}

      <Panel title="Analysis History" sub={`${analyses.length} past analyses`} noPad>
        {analyses.length === 0 ? (
          <div className="empty">No past AI analyses for this server</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Incident</th><th>Confidence</th><th>Occurrences</th><th>Analyzed</th></tr>
              </thead>
              <tbody>
                {analyses.map(a => (
                  <tr key={a.id}>
                    <td>
                      <div style={{ color: 'var(--text-1)', fontWeight: 500 }}>{a.title}</div>
                      {a.root_cause && <div className="muted" style={{ fontSize: 11, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}>{a.root_cause}</div>}
                    </td>
                    <td><Badge tone={a.confidence >= 0.7 ? 'green' : a.confidence >= 0.4 ? 'amber' : 'red'}>{a.confidence != null ? `${Math.round(a.confidence * 100)}%` : '—'}</Badge></td>
                    <td><span className="mono">{a.occurrences ?? 0}</span></td>
                    <td><span className="mono muted">{a.created_at ? formatTime(a.created_at) : '—'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
